import { useMemo, useState } from 'react';
import { FormInput } from '../forms';
import FieldHelpButton from './FieldHelpButton';
import US_STATES from '../../data/usStates';
import {
  getCountiesForState,
  getCountyOptionsForState,
  isValidZip,
  lookupByZip,
  parseZipInput,
} from '../../utils/propertyLocationLookup';

const emptyLocation = () => ({
  propertyZip: '',
  propertyState: '',
  propertyCounty: '',
  mode: 'zip',
});

const parseLocationValue = (value) => {
  if (!value) return emptyLocation();
  if (typeof value === 'object') return { ...emptyLocation(), ...value };
  try {
    return { ...emptyLocation(), ...JSON.parse(value) };
  } catch {
    return emptyLocation();
  }
};

const PropertyLocationInput = ({
  value,
  onChange,
  onSubmit,
  onFieldHelp,
  activeHelpValue,
  helpLabel,
}) => {
  const loc = parseLocationValue(value);
  const [zipError, setZipError] = useState('');
  const questionHelpActive = activeHelpValue === '__question__';

  const wrapWithHelp = (content) => {
    if (!onFieldHelp) return content;
    return (
      <div className="lk-conv-field-with-help">
        <div className="lk-conv-field-with-help__control">{content}</div>
        <FieldHelpButton
          active={questionHelpActive}
          label={helpLabel || 'What does this mean?'}
          onClick={() => onFieldHelp(null, null)}
        />
      </div>
    );
  };

  const countyOptions = useMemo(() => {
    if (loc.mode === 'manual' && loc.propertyState) {
      return getCountyOptionsForState(loc.propertyState);
    }
    const hit = lookupByZip(loc.propertyZip);
    return (hit?.counties || []).map((name) => ({ value: name, label: name }));
  }, [loc.mode, loc.propertyState, loc.propertyZip]);

  const update = (patch) => {
    onChange(JSON.stringify({ ...loc, ...patch }));
  };

  const trySubmit = (next) => {
    const data = { ...loc, ...next };
    if (data.mode === 'zip') {
      const zip = parseZipInput(data.propertyZip);
      if (!isValidZip(zip)) return;
      const hit = lookupByZip(zip);
      if (!hit) {
        setZipError('ZIP not found — try manual entry below.');
        return;
      }
      setZipError('');
      if (hit.counties.length === 1) {
        onSubmit?.({
          propertyZip: zip,
          propertyState: hit.state,
          propertyCounty: hit.counties[0],
        });
        return;
      }
      if (data.propertyCounty && hit.counties.includes(data.propertyCounty)) {
        onSubmit?.({
          propertyZip: zip,
          propertyState: hit.state,
          propertyCounty: data.propertyCounty,
        });
      }
      return;
    }

    if (data.propertyState && data.propertyCounty) {
      onSubmit?.({
        propertyZip: data.propertyZip || '',
        propertyState: data.propertyState,
        propertyCounty: data.propertyCounty,
      });
    }
  };

  if (loc.mode === 'manual') {
    return wrapWithHelp(
      <div className="lk-conv-location">
        <label className="lk-conv-location__label" htmlFor="loc-state">State</label>
        <select
          id="loc-state"
          className="lk-conv-location__select"
          value={loc.propertyState}
          onChange={(e) => update({ propertyState: e.target.value, propertyCounty: '' })}
        >
          <option value="">Select state…</option>
          {US_STATES.map((s) => (
            <option key={s.code} value={s.code}>{s.name}</option>
          ))}
        </select>

        {loc.propertyState && (
          <>
            <label className="lk-conv-location__label" htmlFor="loc-county">County</label>
            <select
              id="loc-county"
              className="lk-conv-location__select"
              value={loc.propertyCounty}
              onChange={(e) => {
                const county = e.target.value;
                update({ propertyCounty: county });
                if (county) {
                  trySubmit({ propertyState: loc.propertyState, propertyCounty: county });
                }
              }}
            >
              <option value="">Select county…</option>
              {getCountiesForState(loc.propertyState).map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </>
        )}

        <button
          type="button"
          className="lk-conv-location__link"
          onClick={() => { setZipError(''); update({ mode: 'zip' }); }}
        >
          I know the ZIP code
        </button>
      </div>,
    );
  }

  const multiCounty = countyOptions.length > 1;

  return wrapWithHelp(
    <div className="lk-conv-location">
      <FormInput
        type="text"
        inputMode="numeric"
        autoComplete="postal-code"
        maxLength={5}
        value={parseZipInput(loc.propertyZip)}
        placeholder="e.g. 90210"
        onChange={(e) => {
          setZipError('');
          update({ propertyZip: parseZipInput(e.target.value), propertyCounty: '' });
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') trySubmit({});
        }}
      />
      {zipError && <p className="lk-conv-location__error" role="alert">{zipError}</p>}

      {multiCounty && isValidZip(loc.propertyZip) && (
        <>
          <p className="lk-conv-location__hint">This ZIP spans multiple counties — pick one:</p>
          <select
            className="lk-conv-location__select"
            value={loc.propertyCounty}
            onChange={(e) => {
              const county = e.target.value;
              update({ propertyCounty: county });
              if (county) trySubmit({ propertyCounty: county });
            }}
          >
            <option value="">Select county…</option>
            {countyOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </>
      )}

      {!multiCounty && isValidZip(loc.propertyZip) && (
        <button
          type="button"
          className="lk-conv-location__continue"
          onClick={() => trySubmit({})}
        >
          Continue
        </button>
      )}

      <button
        type="button"
        className="lk-conv-location__link"
        onClick={() => update({ mode: 'manual', propertyZip: '', propertyCounty: '' })}
      >
        I don&apos;t know the ZIP code
      </button>
    </div>,
  );
};

export default PropertyLocationInput;
